import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { Observable, of } from 'rxjs';
import { AccountsService } from '../../../accounts.service';
import { ApiErrorFactory } from '../../../core/api-errors';
import { BackupService, BackupSiteReply } from '../../../core/backup';
import { ErrorDialog } from '../../dialog';

@Component({
  selector: 'app-backup-dialog',
  templateUrl: './backups.component.html',
  styleUrls: [`./backups.styles.scss`],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BackupDialogComponent {
  siteId: string;
  description: string = new Date().toDateString().concat(' - Manual Backup');
  showStagingCTA = false;
  disableButton = false;
  public waiting = false;
  isStaging = false;
  isExpress$: Observable<boolean> = of(true);
  @Output() backupCreate: EventEmitter<BackupSiteReply> = new EventEmitter<BackupSiteReply>();
  private errorDialogRef: MatDialogRef<ErrorDialog>;

  constructor(
    public dialogRef: MatDialogRef<BackupDialogComponent>,
    public errorDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private backupService: BackupService,
    private apiErrorFactory: ApiErrorFactory,
    private accountsService: AccountsService,
    private snowplow: ProductAnalyticsService,
  ) {
    this.siteId = data.siteId;
    this.isStaging = data.isStaging;
    this.isExpress$ = this.accountsService.isExpress$;
    this.showStagingCTA = data.showStagingCTA;
  }

  onSuccess(): void {
    this.waiting = false;
    this.disableButton = false;
    this.dialogRef.close(true);
  }

  public confirmDialog(): void {
    // TODO: handle create backup and edit description differently somehow
    this.disableButton = true;
    this.createBackup(this.isStaging);
  }

  backupFromProduction(): void {
    this.disableButton = true;
    this.createBackup(false);
  }

  backupFromStaging(): void {
    this.disableButton = true;
    this.createBackup(true);
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }

  displayErrorMessage(message: string): void {
    this.errorDialogRef = this.errorDialog.open(ErrorDialog, {
      data: { errMsg: message },
    });
    // this does not perform this.closeDialog() as we do not want to record an association between an error message
    // and an event that represents the user clicking "Close"
    this.dialogRef.close(false);
  }

  protected createBackup(isStaging: boolean): void {
    this.dialogRef.disableClose = true;
    this.waiting = true;
    this.backupService.create(this.siteId, this.description.trim(), isStaging).subscribe(
      (res) => {
        // It takes time for the empty backup to be inserted into the mysql table
        window.setTimeout(() => {
          this.backupCreate.emit(new BackupSiteReply(res.siteId));
          this.onSuccess();
        }, 3000);
      },
      (err) => {
        this.handleError(err);
      },
    );
  }

  private handleError(errorResponse: HttpErrorResponse): void {
    const parsedError = this.apiErrorFactory.parseErrorResponse(errorResponse);
    this.displayErrorMessage(parsedError.getUserMessage());
    this.dialogRef.disableClose = false;
    this.disableButton = false;
    this.waiting = false;
  }
}
